// Portal Checklist — PWA mobile (Fase 1)
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import checklistService from "../../services/checklistService";
import api from "../../services/api";
import Banner from "../../components/ui/Banner";
import PwaInstallHint from "../../components/ui/PwaInstallHint";
import ThemeToggle from "../../components/layout/ThemeToggle";
import ConfirmModal from "../../components/ui/ConfirmModal";
import { abrirCamera, capturarFoto, pararCamera } from "../../utils/camera";
import { getApiBaseUrl } from "../../services/apiBase";
import {
  loadQueue,
  saveQueue,
  enqueueAction,
  labelAcao,
  buildExecSnapshot,
  clearAcaoErro,
  isConflitoErro,
  descricaoErroSync,
} from "../../utils/checklistSyncQueue";

const fmtDT = (d) => {
  try { return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return d || "—"; }
};
const today = () => new Date().toISOString().slice(0, 10);

const ST_COR = { Pendente: "warning", EmAndamento: "info", Atrasada: "danger", Concluida: "success" };
const FILTROS_STATUS = [
  { id: "", label: "Todos" },
  { id: "Pendente", label: "Pendente" },
  { id: "EmAndamento", label: "Em andamento" },
  { id: "Atrasada", label: "Atrasada" },
  { id: "Concluida", label: "Concluída" },
];

const parseFotos = (fotos) => {
  if (!fotos) return [];
  if (Array.isArray(fotos)) return fotos;
  try {
    const arr = JSON.parse(fotos);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};
const urlFoto = (path) => {
  if (!path) return "";
  if (String(path).startsWith("http")) return path;
  const base = getApiBaseUrl().replace(/\/api$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};
const TABS = [
  { id: "hoje", icon: "bi-calendar-day", label: "Hoje" },
  { id: "historico", icon: "bi-clock-history", label: "Histórico" },
  { id: "perfil", icon: "bi-person", label: "Perfil" },
];
const PERFIL_TABS = [
  { id: "foto", label: "Foto" },
  { id: "senha", label: "Senha" },
  { id: "notificacoes", label: "Notificação" },
  { id: "sessoes", label: "Sessão" },
  { id: "preferencias", label: "Pref." },
];

function gpsAtual() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ latitude: null, longitude: null });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve({ latitude: null, longitude: null }),
      { timeout: 8000, maximumAge: 60000 }
    );
  });
}

export default function PortalChecklist({ onLogout }) {
  const { usuario, logout, condominioAtivo, idCondoAtivo } = useAuth();
  const sair = onLogout || logout;
  const idCondominio = condominioAtivo?.id || idCondoAtivo || usuario?.idCondominio || null;

  const [aba, setAba] = useState("hoje");
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tarefas, setTarefas] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState("");

  // Execução
  const [fase, setFase] = useState(null); // null | briefing | exec | revisao | sucesso
  const [modoLeitura, setModoLeitura] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [idx, setIdx] = useState(0);
  const [respostas, setRespostas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [syncInfo, setSyncInfo] = useState({ pendentes: 0, erros: 0, ultima: null, sincronizando: false });
  const [filaSync, setFilaSync] = useState([]);
  const [syncingClientId, setSyncingClientId] = useState(null);
  const [confirmSair, setConfirmSair] = useState(false);
  const [perfilTab, setPerfilTab] = useState("foto");
  const [perfilData, setPerfilData] = useState(null);
  const [perfilMsg, setPerfilMsg] = useState(null);
  const [sessoes, setSessoes] = useState([]);
  const [senhaForm, setSenhaForm] = useState({ atual: "", nova: "", confirma: "" });
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("checklistPortalPrefs") || "{}"); }
    catch { return {}; }
  });
  const videoRef = React.useRef(null);
  const cameraStreamRef = React.useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);

  const fecharCamera = useCallback(() => {
    pararCamera(cameraStreamRef.current);
    cameraStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const stream = cameraStreamRef.current;
    if (!cameraOn || !video || !stream) return;
    video.srcObject = stream;
    video.play?.().catch(() => {});
  }, [cameraOn]);

  useEffect(() => () => { pararCamera(cameraStreamRef.current); }, []);

  const notify = (type, msg) => setBanner({ type, msg });
  const queueKey = `checklist_sync_queue_${usuario?.id || "u"}_${idCondominio || "c"}`;

  const refreshFila = useCallback(() => {
    const fila = loadQueue(queueKey);
    setFilaSync(fila);
    setSyncInfo((s) => ({
      ...s,
      pendentes: fila.length,
      erros: fila.filter((a) => a.erro).length,
    }));
    return fila;
  }, [queueKey]);

  const enqueueSync = useCallback((tipo, payload) => {
    const fullPayload = {
      ...payload,
      execSnapshot: buildExecSnapshot(ctx?.execucao),
    };
    const next = enqueueAction(queueKey, tipo, fullPayload, filaSync);
    setFilaSync(next);
    setSyncInfo((s) => ({
      ...s,
      pendentes: next.length,
      erros: next.filter((a) => a.erro).length,
    }));
  }, [queueKey, filaSync, ctx?.execucao]);

  const carregarHoje = useCallback(async () => {
    if (!idCondominio) return;
    setLoading(true);
    try {
      const r = await checklistService.portalMinhasTarefas({ idCondominio, data: today() });
      setTarefas(r.data || []);
    } catch (e) { notify("danger", e.message); }
    finally { setLoading(false); }
  }, [idCondominio]);

  const carregarHistorico = useCallback(async () => {
    if (!idCondominio) return;
    try {
      const r = await checklistService.portalMinhasExecucoes({ idCondominio });
      setHistorico(r.data || []);
    } catch (e) { notify("danger", e.message); }
  }, [idCondominio]);

  const carregarPerfil = useCallback(async () => {
    try {
      const r = await api("GET", "/portal/perfil");
      setPerfilData(r.data || null);
      setPerfilMsg(null);
    } catch (e) {
      setPerfilMsg({ type: "danger", text: e.message || "Erro ao carregar perfil." });
    }
  }, []);

  const carregarSessoes = useCallback(async () => {
    try {
      const r = await api("GET", "/portal/perfil/sessoes");
      setSessoes(r.data || []);
    } catch {}
  }, []);

  const aplicarResultadosSync = useCallback((filaCompleta, alvo, resultados) => {
    const byClientId = new Map((resultados || []).map((x) => [x.clientId, x]));
    const alvoIds = new Set(alvo.map((a) => a.clientId));
    let okCount = 0;
    let avisoCount = 0;

    const merged = filaCompleta
      .map((acao) => {
        if (!alvoIds.has(acao.clientId)) return acao;
        const res = byClientId.get(acao.clientId);
        if (res?.ok) {
          okCount++;
          if (res.warning) avisoCount++;
          return null;
        }
        if (res && !res.ok) {
          return {
            ...acao,
            erro: {
              code: res.code,
              mensagem: res.mensagem,
              detalhes: res.detalhes || null,
            },
            aviso: null,
            tentativas: (acao.tentativas || 0) + 1,
          };
        }
        return acao;
      })
      .filter(Boolean);

    const finalFila = saveQueue(queueKey, merged);
    return {
      finalFila,
      okCount,
      avisoCount,
      errosCount: finalFila.filter((a) => a.erro).length,
    };
  }, [queueKey]);

  const processarFilaSync = useCallback(async (onlyClientId = null) => {
    if (!navigator.onLine) return;
    const fila = loadQueue(queueKey);
    const alvo = onlyClientId ? fila.filter((a) => a.clientId === onlyClientId) : fila;
    if (!alvo.length) return;

    setSyncInfo((s) => ({ ...s, sincronizando: true }));
    if (onlyClientId) setSyncingClientId(onlyClientId);
    try {
      const r = await checklistService.portalSync(alvo);
      const { finalFila, okCount, avisoCount, errosCount } = aplicarResultadosSync(
        fila,
        alvo,
        r.resultados
      );

      setFilaSync(finalFila);
      setSyncInfo({
        pendentes: finalFila.length,
        erros: errosCount,
        ultima: new Date().toISOString(),
        sincronizando: false,
      });

      if (okCount > 0 && !finalFila.length) {
        notify("success", "Todas as ações foram sincronizadas.");
        carregarHoje();
      } else if (okCount > 0 && onlyClientId) {
        notify("success", "Ação sincronizada.");
        if (avisoCount > 0) {
          notify("warning", "O servidor tinha uma versão mais recente; dados mesclados.");
        }
        if (!finalFila.length) carregarHoje();
      } else if (errosCount > 0) {
        const conflitos = finalFila.filter(isConflitoErro).length;
        notify(
          "warning",
          conflitos
            ? `${conflitos} conflito(s) com outro usuário — veja em Perfil.`
            : `${errosCount} ação(ões) com erro — veja em Perfil.`
        );
      } else if (okCount > 0) {
        notify("info", `${okCount} sincronizada(s); ${finalFila.length} ainda na fila.`);
      }
    } catch (e) {
      setSyncInfo((s) => ({ ...s, sincronizando: false }));
      notify("danger", e.message);
    } finally {
      setSyncingClientId(null);
    }
  }, [queueKey, carregarHoje, aplicarResultadosSync]);

  const retryAcaoSync = useCallback(async (clientId) => {
    if (!navigator.onLine) {
      notify("warning", "Sem conexão para sincronizar.");
      return;
    }
    const fila = loadQueue(queueKey);
    const limpa = clearAcaoErro(fila, clientId);
    const normalized = saveQueue(queueKey, limpa);
    setFilaSync(normalized);
    await processarFilaSync(clientId);
  }, [queueKey, processarFilaSync]);

  const descartarAcaoSync = useCallback((clientId) => {
    const next = saveQueue(queueKey, filaSync.filter((a) => a.clientId !== clientId));
    setFilaSync(next);
    setSyncInfo((s) => ({
      ...s,
      pendentes: next.length,
      erros: next.filter((a) => a.erro).length,
    }));
  }, [queueKey, filaSync]);

  const salvarSenha = async () => {
    if (!senhaForm.atual || !senhaForm.nova) {
      setPerfilMsg({ type: "warning", text: "Informe senha atual e nova senha." });
      return;
    }
    if (senhaForm.nova.length < 6) {
      setPerfilMsg({ type: "warning", text: "Nova senha deve ter ao menos 6 caracteres." });
      return;
    }
    if (senhaForm.nova !== senhaForm.confirma) {
      setPerfilMsg({ type: "warning", text: "Confirmação da senha não confere." });
      return;
    }
    try {
      await api("POST", "/portal/perfil/senha", {
        senhaAtual: senhaForm.atual,
        novaSenha: senhaForm.nova,
      });
      setSenhaForm({ atual: "", nova: "", confirma: "" });
      setPerfilMsg({ type: "success", text: "Senha alterada com sucesso." });
    } catch (e) {
      setPerfilMsg({ type: "danger", text: e.message || "Erro ao alterar senha." });
    }
  };

  const salvarPreferencias = () => {
    localStorage.setItem("checklistPortalPrefs", JSON.stringify(prefs || {}));
    setPerfilMsg({ type: "success", text: "Preferências salvas neste dispositivo." });
  };

  useEffect(() => {
    if (aba === "hoje") carregarHoje();
    if (aba === "historico") carregarHistorico();
    if (aba === "perfil") {
      refreshFila();
      carregarPerfil();
      carregarSessoes();
    }
  }, [aba, carregarHoje, carregarHistorico, refreshFila, carregarPerfil, carregarSessoes]);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    refreshFila();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshFila]);

  useEffect(() => {
    if (isOnline) processarFilaSync().catch(() => {});
  }, [isOnline, processarFilaSync]);

  const tarefasFiltradas = useMemo(() => {
    if (!filtroStatus) return tarefas;
    return tarefas.filter((t) => t.status === filtroStatus);
  }, [tarefas, filtroStatus]);

  const contagemPorStatus = useMemo(() => {
    const c = { "": tarefas.length };
    FILTROS_STATUS.filter((f) => f.id).forEach((f) => {
      c[f.id] = tarefas.filter((t) => t.status === f.id).length;
    });
    return c;
  }, [tarefas]);

  const mapRespostasPortal = (lista) => (lista || []).map((x) => ({
    ...x,
    fotosArr: parseFotos(x.fotos),
  }));

  const visualizarExecucao = async (idExecucao) => {
    if (!idExecucao) {
      notify("warning", "Execução não encontrada para visualização.");
      return;
    }
    setSalvando(true);
    try {
      const r = await checklistService.portalBuscarExecucao(idExecucao);
      setModoLeitura(true);
      setCtx({
        execucao: r.execucao,
        tarefa: r.tarefa,
        modelo: r.modelo,
      });
      setRespostas(mapRespostasPortal(r.respostas));
      setIdx(0);
      setFase("revisao");
    } catch (e) { notify("danger", e.message); }
    finally { setSalvando(false); }
  };

  const iniciarBriefing = (t) => {
    setModoLeitura(false);
    setCtx({ tarefa: t });
    setFase("briefing");
  };

  const iniciarExecucao = async () => {
    setSalvando(true);
    try {
      const gps = await gpsAtual();
      const r = await checklistService.portalIniciar({
        idTarefa: ctx.tarefa.id,
        ...gps,
        dispositivo: navigator.userAgent?.slice(0, 200),
      });
      setCtx({ ...ctx, execucao: r.execucao, tarefa: r.tarefa, modelo: r.modelo });
      setRespostas(mapRespostasPortal(r.respostas));
      setModoLeitura(false);
      const primeiroPend = (r.respostas || []).findIndex((x) => !x.resposta);
      setIdx(primeiroPend >= 0 ? primeiroPend : 0);
      setFase("exec");
    } catch (e) { notify("danger", e.message); }
    finally { setSalvando(false); }
  };

  const itemAtual = respostas[idx];
  const progresso = respostas.length ? Math.round(((idx + 1) / respostas.length) * 100) : 0;

  const respostaNao = (r) => r?.resposta === "Não" || r?.resposta === "Nao";
  const exigeComentarioNc = (it) =>
    it?.tpresposta === "SimNao" && (it.geranc === 1 || it.geranc === true) && respostaNao(it);

  const atualizarItem = (campo, valor) => {
    setRespostas((prev) => prev.map((r, i) => (i === idx ? { ...r, [campo]: valor } : r)));
  };

  const validarItemLocal = (it) => {
    if (!it) return null;
    if (it.obrigatorio === 1 || it.obrigatorio === true) {
      if (it.tpresposta === "Foto") {
        if (!it.fotosArr?.length) return "Adicione ao menos uma foto.";
      } else if (!it.resposta?.toString().trim()) return "Resposta obrigatória.";
    }
    if (exigeComentarioNc(it)) {
      if (!it.comentario?.trim()) return "Informe comentário para a não conformidade.";
    }
    return null;
  };

  const salvarItemAtual = async () => {
    if (modoLeitura) return true;
    const err = validarItemLocal(itemAtual);
    if (err) { notify("warning", err); return false; }
    setSalvando(true);
    try {
      const payload = {
        indiceAtual: idx,
        respostas: [{
          idItemModelo: itemAtual.iditemmodelo,
          resposta: itemAtual.resposta,
          comentario: itemAtual.comentario || "",
          fotos: itemAtual.fotosArr || [],
        }],
      };
      if (!navigator.onLine) {
        enqueueSync("salvar", { idExecucao: ctx.execucao.id, ...payload });
        notify("info", "Sem internet: resposta salva localmente para sincronização.");
      } else {
        const r = await checklistService.portalSalvarRespostas(ctx.execucao.id, payload);
        if (r?.execucao) {
          setCtx((c) => (c ? { ...c, execucao: r.execucao } : c));
        }
      }
      return true;
    } catch (e) { notify("danger", e.message); return false; }
    finally { setSalvando(false); }
  };

  const proximo = async () => {
    if (!modoLeitura) {
      const ok = await salvarItemAtual();
      if (!ok) return;
    }
    if (idx >= respostas.length - 1) setFase("revisao");
    else setIdx(idx + 1);
  };

  const anterior = async () => {
    if (!modoLeitura) await salvarItemAtual();
    if (idx > 0) setIdx(idx - 1);
  };

  const finalizar = async () => {
    for (const it of respostas) {
      const err = validarItemLocal(it);
      if (err) { notify("warning", `Item "${it.pergunta}": ${err}`); return; }
    }
    if (!window.confirm("Confirmar conclusão do checklist?")) return;
    setSalvando(true);
    try {
      const savePayload = {
        respostas: respostas.map((r) => ({
          idItemModelo: r.iditemmodelo,
          resposta: r.resposta,
          comentario: r.comentario || "",
          fotos: r.fotosArr || [],
        })),
      };
      const gps = await gpsAtual();
      if (!navigator.onLine) {
        enqueueSync("concluir", {
          idExecucao: ctx.execucao.id,
          ...gps,
          respostas: savePayload.respostas,
        });
        setCtx({ ...ctx, resultado: { ...ctx.execucao, qtd_nc: respostas.filter((x) => x.geranc && (x.resposta === "Não" || x.resposta === "Nao")).length } });
        notify("info", "Conclusão registrada offline. Sincronizaremos quando voltar a internet.");
      } else {
        await checklistService.portalSalvarRespostas(ctx.execucao.id, savePayload);
        const r = await checklistService.portalConcluir(ctx.execucao.id, gps);
        setCtx({ ...ctx, resultado: r.execucao });
      }
      setFase("sucesso");
      carregarHoje();
    } catch (e) { notify("danger", e.message); }
    finally { setSalvando(false); }
  };

  const sairExec = () => {
    fecharCamera();
    setModoLeitura(false);
    setFase(null);
    setCtx(null);
    setRespostas([]);
    setIdx(0);
    carregarHoje();
    if (aba === "historico") carregarHistorico();
  };

  useEffect(() => {
    fecharCamera();
  }, [idx, fecharCamera]);

  const tirarFoto = async () => {
    try {
      if (!cameraOn) {
        const stream = await abrirCamera();
        cameraStreamRef.current = stream;
        setCameraOn(true);
        return;
      }
      if (!videoRef.current?.videoWidth) {
        notify("warning", "Aguarde a câmera carregar e tente novamente.");
        return;
      }
      setUploadingFoto(true);
      const dataUrl = capturarFoto(videoRef.current);
      const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
      const up = await checklistService.portalUploadFoto({ base64, mimeType: "image/jpeg" });
      if (!up?.url) throw new Error("Falha ao enviar a foto.");
      const base = getApiBaseUrl().replace(/\/api$/, "");
      const url = up.url.startsWith("http") ? up.url : `${base}${up.url}`;
      setRespostas((prev) => prev.map((r, i) => {
        if (i !== idx) return r;
        const fotosArr = [...(r.fotosArr || []), url];
        return {
          ...r,
          fotosArr,
          resposta: r.tpresposta === "Foto" ? "ok" : r.resposta,
        };
      }));
      notify("success", "Foto salva. Você pode capturar outra ou fechar a câmera.");
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Permissão de câmera negada. Permita o acesso no navegador."
        : e?.name === "NotFoundError"
          ? "Nenhuma câmera encontrada neste dispositivo."
          : (e.message || "Erro na câmera.");
      notify("danger", msg);
      fecharCamera();
    } finally {
      setUploadingFoto(false);
    }
  };

  const removerFotoItem = (indiceFoto) => {
    setRespostas((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const fotosArr = (r.fotosArr || []).filter((_, j) => j !== indiceFoto);
      return {
        ...r,
        fotosArr,
        resposta: r.tpresposta === "Foto" && fotosArr.length ? "ok" : "",
      };
    }));
  };

  const renderRespostaSomenteLeitura = () => {
    if (!itemAtual) return null;
    const tp = itemAtual.tpresposta;
    const fotos = (itemAtual.fotosArr || []).map(urlFoto);
    if (tp === "Foto") {
      return (
        <div className="my-3">
          {fotos.length === 0 ? (
            <p className="text-muted">Nenhuma foto registrada.</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {fotos.map((url, i) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={`Foto ${i + 1}`} className="rounded" style={{ width: 88, height: 88, objectFit: "cover" }} />
                </a>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (tp === "SimNao") {
      return (
        <div className="my-3">
          <span className="badge bg-primary fs-6">{itemAtual.resposta || "—"}</span>
        </div>
      );
    }
    return (
      <div className="my-3 p-3 bg-light rounded">
        <div className="fw-medium">{itemAtual.resposta || "—"}</div>
        {itemAtual.comentario && (
          <div className="small text-muted mt-2">{itemAtual.comentario}</div>
        )}
        {fotos.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {fotos.map((url, i) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt="" className="rounded" style={{ width: 64, height: 64, objectFit: "cover" }} />
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderResposta = () => {
    if (!itemAtual) return null;
    if (modoLeitura) return renderRespostaSomenteLeitura();
    const tp = itemAtual.tpresposta;
    if (tp === "SimNao") {
      return (
        <div className="d-flex gap-2 justify-content-center my-3">
          {["Sim", "Não"].map((v) => (
            <button key={v} type="button"
              className={`btn btn-lg flex-fill ${itemAtual.resposta === v ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => atualizarItem("resposta", v)}>{v}</button>
          ))}
        </div>
      );
    }
    if (tp === "Opcoes") {
      const opts = (itemAtual.opcoes || "").split(";").map((s) => s.trim()).filter(Boolean);
      return (
        <div className="d-flex flex-column gap-2 my-3">
          {opts.map((o) => (
            <button key={o} type="button"
              className={`btn ${itemAtual.resposta === o ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => atualizarItem("resposta", o)}>{o}</button>
          ))}
        </div>
      );
    }
    if (tp === "Numero") {
      return (
        <input type="number" className="form-control form-control-lg my-3" value={itemAtual.resposta || ""}
          onChange={(e) => atualizarItem("resposta", e.target.value)} />
      );
    }
    if (tp === "Foto") {
      const fotos = itemAtual.fotosArr || [];
      return (
        <div className="my-3">
          <p className="small text-muted mb-2">
            {fotos.length === 0
              ? "Tire ao menos uma foto. É possível adicionar várias."
              : `${fotos.length} foto(s) — toque em Capturar para adicionar outra.`}
          </p>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {fotos.map((url, i) => (
              <div key={`${url}-${i}`} className="position-relative">
                <img src={url} alt={`Foto ${i + 1}`} className="rounded" style={{ width: 72, height: 72, objectFit: "cover" }} />
                <button
                  type="button"
                  className="btn btn-danger btn-sm position-absolute top-0 end-0 rounded-circle p-0"
                  style={{ width: 22, height: 22, fontSize: 14, lineHeight: 1 }}
                  onClick={() => removerFotoItem(i)}
                  aria-label={`Remover foto ${i + 1}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {cameraOn && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-100 rounded mb-2"
              style={{ maxHeight: 240, background: "#000" }}
            />
          )}
          <div className="d-flex flex-column gap-2">
            <button
              type="button"
              className="btn btn-outline-primary w-100"
              disabled={uploadingFoto}
              onClick={tirarFoto}
            >
              <i className="bi bi-camera me-1" />
              {uploadingFoto
                ? "Enviando..."
                : cameraOn
                  ? "Capturar foto"
                  : fotos.length
                    ? "Adicionar outra foto"
                    : "Abrir câmera"}
            </button>
            {cameraOn && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={fecharCamera}>
                Fechar câmera
              </button>
            )}
          </div>
        </div>
      );
    }
    return (
      <textarea className="form-control my-3" rows={3} value={itemAtual.resposta || ""}
        onChange={(e) => atualizarItem("resposta", e.target.value)} placeholder="Sua resposta" />
    );
  };

  // ── Execução full-screen ───────────────────────────────────────────────────
  if (fase === "briefing" && ctx?.tarefa) {
    const t = ctx.tarefa;
    return (
      <div className="pcp-page d-flex flex-column min-vh-100">
        <div className="pcp-content-area flex-grow-1 p-3">
          <button className="btn btn-link ps-0" onClick={sairExec}><i className="bi bi-arrow-left" /> Voltar</button>
          <div className="pcp-card p-3 mt-2">
            <h5 className="fw-bold">{t.nmrotina}</h5>
            <p className="text-muted mb-1">{t.nmmodelo}</p>
            {t.nmlocal && <p className="mb-1"><i className="bi bi-geo-alt me-1" />{t.nmlocal}</p>}
            <p className="mb-0">Prazo: {fmtDT(t.dtlimite)}</p>
            {t.status === "Atrasada" && <div className="alert alert-danger py-2 mt-2 mb-0 small">Tarefa atrasada</div>}
          </div>
          <button className="btn btn-primary btn-lg w-100 mt-4" disabled={salvando} onClick={iniciarExecucao}>
            {salvando ? "Iniciando..." : "Iniciar checklist"}
          </button>
        </div>
      </div>
    );
  }

  if (fase === "exec" && itemAtual) {
    return (
      <div className="pcp-page d-flex flex-column min-vh-100">
        <div className="px-3 pt-2 d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => (modoLeitura ? sairExec() : setConfirmSair(true))}
          >
            <i className="bi bi-x-lg" />
          </button>
          <span className="small text-muted">
            {modoLeitura && <span className="badge bg-secondary me-2">Somente leitura</span>}
            Item {idx + 1} de {respostas.length}
          </span>
        </div>
        <div className="px-3"><div className="progress mb-3" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${progresso}%` }} /></div></div>
        <div className="pcp-content-area flex-grow-1 px-3 pb-5">
          <h6 className="fw-semibold mb-3">{itemAtual.pergunta}</h6>
          {renderResposta()}
          {exigeComentarioNc(itemAtual) && (
            <>
              <label className="form-label small text-muted mt-2">Comentário (obrigatório para não conformidade)</label>
              <textarea className="form-control" rows={2} value={itemAtual.comentario || ""}
                onChange={(e) => atualizarItem("comentario", e.target.value)}
                placeholder="Descreva o que foi identificado..." />
            </>
          )}
        </div>
        <div className="border-top p-3 d-flex gap-2 bg-white" style={{ position: "sticky", bottom: 0 }}>
          <button type="button" className="btn btn-outline-secondary" disabled={idx === 0 || salvando} onClick={anterior}>
            Anterior
          </button>
          <button type="button" className="btn btn-primary flex-grow-1" disabled={salvando} onClick={proximo}>
            {idx >= respostas.length - 1 ? (modoLeitura ? "Resumo" : "Revisar") : "Próximo"}
          </button>
        </div>
        {!modoLeitura && confirmSair && (
          <ConfirmModal
            titulo="Salvar e sair?"
            mensagem="O progresso já salvo será mantido. Deseja sair?"
            textoOk="Sair"
            onConfirm={async () => { await salvarItemAtual(); setConfirmSair(false); sairExec(); }}
            onCancel={() => setConfirmSair(false)}
          />
        )}
      </div>
    );
  }

  if (fase === "revisao") {
    const nc = respostas.filter((r) => r.conforme === 0 || (r.geranc && (r.resposta === "Não" || r.resposta === "Nao"))).length;
    const ex = ctx?.execucao;
    return (
      <div className="pcp-page p-3 min-vh-100">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h5 className="fw-bold mb-0">{modoLeitura ? "Checklist concluído" : "Revisão"}</h5>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={sairExec}>Fechar</button>
        </div>
        {modoLeitura && ex && (
          <div className="small text-muted mb-2">
            <div>Início: {fmtDT(ex.dtinicio)}</div>
            <div>Fim: {fmtDT(ex.dtfim)}</div>
          </div>
        )}
        <p>{respostas.length} itens · {nc} inconformidade(s)</p>
        {modoLeitura && (
          <div className="alert alert-info py-2 small mb-3">
            Visualização somente leitura. Não é possível alterar respostas após a conclusão.
          </div>
        )}
        <ul className="list-group mb-3">
          {respostas.map((r, i) => (
            <li key={r.id} className="list-group-item d-flex justify-content-between align-items-start" role="button"
              onClick={() => { setIdx(i); setFase("exec"); }}>
              <span>{r.pergunta}</span>
              <span className="badge bg-secondary">
                {r.tpresposta === "Foto" && (r.fotosArr?.length || 0) > 0
                  ? `${r.fotosArr.length} foto(s)`
                  : (r.resposta || "—")}
              </span>
            </li>
          ))}
        </ul>
        {!modoLeitura && (
          <button type="button" className="btn btn-success btn-lg w-100" disabled={salvando} onClick={finalizar}>
            {salvando ? "Finalizando..." : "Finalizar checklist"}
          </button>
        )}
        {!modoLeitura && (
          <button type="button" className="btn btn-link w-100 mt-2" onClick={() => setFase("exec")}>Voltar</button>
        )}
      </div>
    );
  }

  if (fase === "sucesso" && ctx?.resultado) {
    return (
      <div className="pcp-page p-3 min-vh-100 text-center">
        <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 64 }} />
        <h4 className="mt-3">Checklist concluído</h4>
        <p className="text-muted">{ctx.tarefa.nmrotina}</p>
        <p>Inconformidades: {ctx.resultado.qtd_nc || 0}</p>
        <button className="btn btn-primary w-100 mt-4" onClick={sairExec}>Voltar ao início</button>
      </div>
    );
  }

  // ── Shell portal ───────────────────────────────────────────────────────────
  return (
    <div className="pcp-page d-flex flex-column min-vh-100">
      <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom pcp-notif-bar">
        <div>
          <div className="fw-bold" style={{ fontSize: 15 }}>Checklist</div>
          <div className="text-muted" style={{ fontSize: 12 }}>{condominioAtivo?.nmFantasia || condominioAtivo?.nmCondominio || "—"}</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setAba("perfil")}
            title="Meu perfil"
          >
            <i className="bi bi-person-badge me-1" />
            {usuario?.nome?.split(" ")[0] || "Meu perfil"}
          </button>
          {!isOnline && <span className="badge bg-warning text-dark">Offline</span>}
          {syncInfo.pendentes > 0 && (
            <span className={`badge ${syncInfo.erros > 0 ? "bg-danger" : "bg-info"}`}>
              {syncInfo.erros > 0 ? `Sync erro: ${syncInfo.erros}` : `Sync: ${syncInfo.pendentes}`}
            </span>
          )}
          <ThemeToggle />
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={sair}>Sair</button>
        </div>
      </div>

      {banner && (
        <div className="px-3 pt-2"><Banner tipo={banner.type} onClose={() => setBanner(null)}>{banner.msg}</Banner></div>
      )}

      <div className="pcp-content-area flex-grow-1 p-3 pb-5">
        {aba === "hoje" && (
          <>
            <PwaInstallHint />
            <div className="d-flex flex-wrap gap-2 mb-3">
              {FILTROS_STATUS.map((f) => (
                <button
                  key={f.id || "todos"}
                  type="button"
                  className={`btn btn-sm ${filtroStatus === f.id ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setFiltroStatus(f.id)}
                >
                  {f.label}
                  <span className="ms-1 opacity-75">({contagemPorStatus[f.id] ?? 0})</span>
                </button>
              ))}
            </div>
            {loading && <p className="text-muted">Carregando...</p>}
            {!loading && tarefasFiltradas.map((t) => (
              <div key={t.id} className="pcp-card p-3 mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-semibold">{t.nmrotina}</div>
                    <div className="small text-muted">{t.nmlocal || t.nmmodelo}</div>
                    {!t.idresponsavel && (
                      <div className="small text-primary">
                        <i className="bi bi-person-plus me-1" />
                        Sem responsável (disponível para assumir)
                      </div>
                    )}
                    <div className="small">
                      <strong>Início previsto:</strong> {fmtDT(t.dtprevista)}
                    </div>
                    <div className="small">
                      <strong>Prazo:</strong> {fmtDT(t.dtlimite)}
                    </div>
                  </div>
                  <span className={`badge bg-${ST_COR[t.status] || "secondary"}`}>{t.status}</span>
                </div>
                {t.status !== "Concluida" && (
                  <button className="btn btn-primary w-100 mt-3" onClick={() => iniciarBriefing(t)}>
                    {t.status === "EmAndamento" ? "Continuar" : "Iniciar checklist"}
                  </button>
                )}
                {t.status === "Concluida" && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 mt-3"
                    disabled={salvando || !t.idexecucao_concluida}
                    onClick={() => visualizarExecucao(t.idexecucao_concluida)}
                  >
                    <i className="bi bi-eye me-1" />Visualizar checklist
                  </button>
                )}
              </div>
            ))}
            {!loading && tarefas.length > 0 && !tarefasFiltradas.length && (
              <div className="text-center text-muted py-5">Nenhuma tarefa com este status.</div>
            )}
            {!loading && !tarefas.length && (
              <div className="text-center text-muted py-5">Nenhuma tarefa para hoje.</div>
            )}
          </>
        )}

        {aba === "historico" && (
          <div>
            {historico.map((e) => (
              <div key={e.id} className="pcp-card p-3 mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-semibold">{e.nmrotina}</div>
                    <div className="small text-muted">{e.nmlocal || e.nmmodelo}</div>
                    <div className="small text-muted">{fmtDT(e.dtfim)} · NC: {e.qtd_nc || 0}</div>
                  </div>
                  <span className="badge bg-success">Concluída</span>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm w-100 mt-2"
                  disabled={salvando}
                  onClick={() => visualizarExecucao(e.id)}
                >
                  <i className="bi bi-eye me-1" />Visualizar
                </button>
              </div>
            ))}
            {!historico.length && <p className="text-muted text-center py-4">Sem histórico.</p>}
          </div>
        )}

        {aba === "perfil" && (
          <div>
            <div className="pcp-card p-2 mb-3">
              <div className="d-flex flex-wrap gap-2">
                {PERFIL_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`btn btn-sm ${perfilTab === t.id ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setPerfilTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {perfilMsg && (
              <div className={`alert alert-${perfilMsg.type === "danger" ? "danger" : perfilMsg.type === "warning" ? "warning" : "success"} p-2`}>
                {perfilMsg.text}
              </div>
            )}

            <div className="pcp-card p-3 mb-3">
              <div className="fw-bold mb-2">Meu Perfil</div>
              <div className="fw-semibold">{usuario?.nome}</div>
              <div className="text-muted small">{usuario?.email}</div>
              <div className="text-muted small mt-2">Perfil: {usuario?.perfil}</div>
              <div className="small mt-2">
                Conexão: {isOnline ? <span className="text-success">Online</span> : <span className="text-warning">Offline</span>}
              </div>
              {syncInfo.ultima && (
                <div className="text-muted small">Última sync: {fmtDT(syncInfo.ultima)}</div>
              )}
            </div>

            {perfilTab === "foto" && (
              <div className="pcp-card p-3 mb-3">
                <div className="fw-semibold mb-2">Foto de Perfil</div>
                {perfilData?.condomino?.avatar ? (
                  <img src={perfilData.condomino.avatar} alt="avatar" className="rounded-circle mb-2" style={{ width: 72, height: 72, objectFit: "cover" }} />
                ) : (
                  <div className="text-muted small">Sem foto cadastrada.</div>
                )}
              </div>
            )}

            {perfilTab === "senha" && (
              <div className="pcp-card p-3 mb-3">
                <div className="fw-semibold mb-2">Trocar Senha</div>
                <input type="password" className="form-control mb-2" placeholder="Senha atual"
                  value={senhaForm.atual} onChange={(e) => setSenhaForm((s) => ({ ...s, atual: e.target.value }))} />
                <input type="password" className="form-control mb-2" placeholder="Nova senha"
                  value={senhaForm.nova} onChange={(e) => setSenhaForm((s) => ({ ...s, nova: e.target.value }))} />
                <input type="password" className="form-control mb-2" placeholder="Confirmar nova senha"
                  value={senhaForm.confirma} onChange={(e) => setSenhaForm((s) => ({ ...s, confirma: e.target.value }))} />
                <button type="button" className="btn btn-primary btn-sm" onClick={salvarSenha}>Salvar senha</button>
              </div>
            )}

            {perfilTab === "notificacoes" && (
              <div className="pcp-card p-3 mb-3">
                <div className="fw-semibold mb-2">Notificações</div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={!!prefs.pushChecklist}
                    onChange={(e) => setPrefs((p) => ({ ...p, pushChecklist: e.target.checked }))} />
                  <label className="form-check-label">Receber aviso de novas tarefas</label>
                </div>
                <button type="button" className="btn btn-primary btn-sm mt-3" onClick={salvarPreferencias}>Salvar preferências</button>
              </div>
            )}

            {perfilTab === "sessoes" && (
              <div className="pcp-card p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold">Sessões Ativas</div>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={carregarSessoes}>Atualizar</button>
                </div>
                {!sessoes.length && <div className="text-muted small">Nenhuma sessão ativa encontrada.</div>}
                {sessoes.map((s) => (
                  <div key={s.id} className="border rounded p-2 mb-2">
                    <div className="small fw-semibold">{s.deviceLabel || "Dispositivo"}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {s.ip || "IP —"} · Último acesso: {fmtDT(s.dtUltimoAcesso)}
                    </div>
                    {s.atual && <span className="badge bg-success mt-1">Sessão atual</span>}
                  </div>
                ))}
              </div>
            )}

            {perfilTab === "preferencias" && (
              <div className="pcp-card p-3 mb-3">
                <div className="fw-semibold mb-2">Preferências</div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={!!prefs.compactMode}
                    onChange={(e) => setPrefs((p) => ({ ...p, compactMode: e.target.checked }))} />
                  <label className="form-check-label">Modo compacto</label>
                </div>
                <button type="button" className="btn btn-primary btn-sm mt-3" onClick={salvarPreferencias}>Salvar preferências</button>
              </div>
            )}

            <div className="pcp-card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Fila de sincronização</div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={!filaSync.length || !isOnline || syncInfo.sincronizando}
                  onClick={() => processarFilaSync(null)}
                >
                  {syncInfo.sincronizando && !syncingClientId ? "Sincronizando…" : "Sincronizar tudo"}
                </button>
              </div>
              {!filaSync.length && (
                <p className="text-muted small mb-0">Nenhuma ação pendente.</p>
              )}
              {filaSync.map((acao) => (
                <div
                  key={acao.clientId}
                  className={`border rounded p-2 mb-2 ${
                    isConflitoErro(acao) ? "border-warning bg-warning bg-opacity-10" : acao.erro ? "border-danger" : ""
                  }`}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="small fw-semibold">{labelAcao(acao)}</div>
                    {isConflitoErro(acao) && (
                      <span className="badge bg-warning text-dark">Conflito</span>
                    )}
                  </div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {fmtDT(acao.ts)}
                    {acao.tentativas > 0 ? ` · ${acao.tentativas} tentativa(s)` : ""}
                  </div>
                  {acao.erro && (
                    <div className={`small mt-1 ${isConflitoErro(acao) ? "text-warning-emphasis" : "text-danger"}`}>
                      {isConflitoErro(acao) ? (
                        <>
                          <i className="bi bi-person-exclamation me-1" />
                          {descricaoErroSync(acao)}
                        </>
                      ) : (
                        <>
                          {acao.erro.code ? `[${acao.erro.code}] ` : ""}{acao.erro.mensagem}
                        </>
                      )}
                    </div>
                  )}
                  {acao.aviso && (
                    <div className="small text-info mt-1">
                      <i className="bi bi-info-circle me-1" />
                      {acao.aviso.mensagem || acao.aviso.message}
                    </div>
                  )}
                  <div className="d-flex gap-2 mt-2 flex-wrap">
                    {isOnline && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={syncInfo.sincronizando}
                        onClick={() => (acao.erro ? retryAcaoSync(acao.clientId) : processarFilaSync(acao.clientId))}
                      >
                        {syncingClientId === acao.clientId
                          ? "Sincronizando…"
                          : acao.erro ? "Tentar só esta" : "Sincronizar esta"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => descartarAcaoSync(acao.clientId)}
                    >
                      Descartar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="pcp-card p-3">
              <PwaInstallHint />
            </div>
          </div>
        )}
      </div>

      <div className="border-top d-flex pcp-tabs" style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
        {TABS.map((t) => (
          <button key={t.id} type="button"
            className={`pcp-tab-btn flex-fill ${aba === t.id ? "active" : ""}`}
            onClick={() => setAba(t.id)}>
            <i className={`bi ${t.icon} d-block`} style={{ fontSize: 18 }} />
            <span style={{ fontSize: 11 }}>{t.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
